import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL as API_BASE } from "../config";
import PageHeader from "../components/ui/PageHeader";
import { panelCardSx } from "../theme/surfaceStyles";
import { lineKindOf } from "./planner/plannerQuote";
import { LABOR_CATEGORIES } from "./planner/plannerRates";
import { PLANNER_CHILD_LINE_KINDS, plannerKindAccent } from "./planner/plannerTopics";
import LogJobActionDialog from "./planner/LogJobActionDialog";
import { formatMoney } from "./quotes/quotePricingMath";

const PLANNER_API = `${API_BASE}/planner`;

const CHILD_SECTIONS = [
    { id: "component", label: "Components" },
    { id: "labor", label: "Labor" },
    { id: "purchase", label: "Purchases" },
];

function formatHours(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "0 hrs";
    const label = Math.abs(n) === 1 ? "hr" : "hrs";
    return `${n.toLocaleString(undefined, { maximumFractionDigits: n % 1 ? 2 : 0 })} ${label}`;
}

function categoryLabel(id) {
    return LABOR_CATEGORIES.find((row) => row.id === id)?.label || id || "Labor";
}

function lineKindLabel(kind) {
    const all = [{ id: "job", label: "Job" }, ...PLANNER_CHILD_LINE_KINDS];
    return all.find((row) => row.id === kind)?.label || kind;
}

function childMap(items) {
    const map = new Map();
    for (const item of items || []) {
        const key = item.parentId || "";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    }
    return map;
}

function rootIdOf(item, lookup) {
    let current = item;
    const seen = new Set();
    while (current?.parentId && lookup.has(current.parentId) && !seen.has(current.id)) {
        seen.add(current.id);
        current = lookup.get(current.parentId);
    }
    return current?.id || item?.id || "";
}

function crumbPath(item, lookup) {
    const path = [];
    let current = item;
    const seen = new Set();
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.unshift(current);
        current = current.parentId ? lookup.get(current.parentId) : null;
    }
    return path;
}

function quotedHoursOf(item) {
    if (!item) return 0;
    const kind = lineKindOf(item);
    if (kind === "labor") return Number(item.quoteOwnLaborHours || item.laborEstimate) || 0;
    return Number(item.quoteRollupLaborHours) || 0;
}

function kindSurfaceSx(theme, kind, { hover = false } = {}) {
    const accent = plannerKindAccent(theme, kind);
    const useFill = kind !== "component";
    const fill = alpha(accent, theme.palette.mode === "dark" ? 0.14 : 0.08);
    return {
        bgcolor: useFill ? fill : theme.palette.background.paper,
        border: `1px solid ${alpha(accent, useFill ? 0.38 : 0.22)}`,
        borderLeft: `4px solid ${accent}`,
        ...(hover
            ? {
                "&:hover": {
                    borderColor: alpha(accent, 0.6),
                    boxShadow: `0 4px 18px ${alpha(accent, theme.palette.mode === "dark" ? 0.18 : 0.12)}`,
                },
            }
            : {}),
    };
}

function LineKindChip({ kind }) {
    return (
        <Chip
            size="small"
            label={lineKindLabel(kind)}
            sx={(theme) => {
                const accent = plannerKindAccent(theme, kind);
                return {
                    bgcolor: alpha(accent, kind === "component" ? 0.08 : 0.22),
                    color: accent,
                    border: `1px solid ${alpha(accent, 0.45)}`,
                    fontWeight: 800,
                };
            }}
        />
    );
}

function HoursBar({ logged = 0, quoted = 0 }) {
    const pct = quoted > 0 ? Math.min(100, Math.round((logged / quoted) * 100)) : logged ? 100 : 0;
    const over = quoted > 0 && logged > quoted;
    return (
        <Box sx={{ minWidth: 120, flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {formatHours(logged)} logged
                </Typography>
                <Typography variant="caption" color={over ? "warning.main" : "text.secondary"}>
                    {quoted ? `${formatHours(quoted)} quoted` : "No quote hrs"}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={pct}
                color={over ? "warning" : "primary"}
                sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
            />
        </Box>
    );
}

function Metric({ label, value, hint }) {
    return (
        <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, minWidth: 0, flex: "1 1 140px" }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Paper>
    );
}

function WorkCard({ item, childCount, lastLog, onOpen, onLogTime, onLogMaterial }) {
    const kind = lineKindOf(item);
    const logged = Number(item.actualHours) || 0;
    const quoted = quotedHoursOf(item);
    const materials = Number(item.extraMaterialCost) || 0;
    return (
        <Paper
            sx={(theme) => ({
                ...panelCardSx(theme, { hover: true }),
                ...kindSurfaceSx(theme, kind, { hover: true }),
                display: "flex",
                alignItems: "stretch",
                overflow: "hidden",
            })}
        >
            <Box
                onClick={() => onOpen(item)}
                sx={{ flex: 1, minWidth: 0, px: 1.75, py: 1.5, cursor: "pointer" }}
            >
                <Stack spacing={0.75}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <LineKindChip kind={kind} />
                        {kind === "labor" && item.laborCategory ? (
                            <Chip size="small" variant="outlined" label={categoryLabel(item.laborCategory)} />
                        ) : null}
                        {logged ? (
                            <Chip size="small" color={quoted && logged > quoted ? "warning" : "success"} variant="outlined" label={formatHours(logged)} />
                        ) : (
                            <Chip size="small" variant="outlined" label="No time yet" />
                        )}
                        {materials ? <Chip size="small" color="warning" variant="outlined" label={formatMoney(materials)} /> : null}
                    </Stack>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                        {item.title}
                    </Typography>
                    <HoursBar logged={logged} quoted={quoted} />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {childCount ? (
                            <Typography variant="caption" color="text.secondary">
                                {childCount} child{childCount === 1 ? "" : "ren"}
                            </Typography>
                        ) : null}
                        {lastLog ? (
                            <Typography variant="caption" color="text.secondary">
                                Last: {lastLog.occurredOn}
                                {lastLog.body ? ` · ${lastLog.body}` : ""}
                            </Typography>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                Nothing logged on this line
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Box>
            <Stack
                justifyContent="center"
                sx={(theme) => ({
                    pr: 0.5,
                    py: 0.5,
                    borderLeft: 1,
                    borderColor: "divider",
                    bgcolor: kind === "component" ? "transparent" : alpha(plannerKindAccent(theme, kind), 0.12),
                })}
            >
                {kind === "labor" || kind === "job" || kind === "component" ? (
                    <Tooltip title="Log time">
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onLogTime(item);
                            }}
                            aria-label={`Log time on ${item.title}`}
                        >
                            <MoreTimeIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {kind === "purchase" || kind === "component" || kind === "job" ? (
                    <Tooltip title="Extra material">
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onLogMaterial(item);
                            }}
                            aria-label={`Log material on ${item.title}`}
                        >
                            <Inventory2Icon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <IconButton onClick={() => onOpen(item)} aria-label={`Open ${item.title}`}>
                    <ChevronRightIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
}

export default function JobActions() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [actions, setActions] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    const [logOpen, setLogOpen] = useState(false);
    const [logKind, setLogKind] = useState("time");
    const [logItemId, setLogItemId] = useState("");
    const [logJobOverride, setLogJobOverride] = useState(null);
    const [snackbar, setSnack] = useState({ open: false, message: "", severity: "success" });

    const jobId = searchParams.get("job") || "";
    const focusId = searchParams.get("item") || jobId;

    const toast = useCallback((message, severity = "success") => {
        setSnack({ open: true, message, severity });
    }, []);

    const applyBundle = useCallback((payload) => {
        if (!payload) return;
        setItems(payload.items || []);
        setActions(payload.actions || []);
        if (payload.customers) setCustomers(payload.customers);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(PLANNER_API, { withCredentials: true });
            applyBundle(res.data || {});
        } catch (err) {
            toast(err.response?.data?.error || err.message || "Unable to load jobs.", "error");
        } finally {
            setLoading(false);
        }
    }, [applyBundle, toast]);

    useEffect(() => {
        load();
    }, [load]);

    async function act(action, extra = {}) {
        setBusy(true);
        try {
            const res = await axios.post(PLANNER_API, { action, ...extra }, { withCredentials: true });
            applyBundle(res.data || {});
            return res.data;
        } catch (err) {
            toast(err.response?.data?.error || err.message || "Unable to save.", "error");
            throw err;
        } finally {
            setBusy(false);
        }
    }

    const byId = useMemo(() => new Map(items.map((row) => [row.id, row])), [items]);
    const kidsByParent = useMemo(() => childMap(items), [items]);
    const jobs = useMemo(() => items.filter((row) => !row.parentId && !row.archived), [items]);
    const customersById = useMemo(() => new Map(customers.map((row) => [row.id, row])), [customers]);
    const job = byId.get(jobId) || null;
    const focus = byId.get(focusId) || job;
    const crumbs = useMemo(() => (focus ? crumbPath(focus, byId) : []), [focus, byId]);
    const parentItem = focus?.parentId ? byId.get(focus.parentId) : null;

    const visibleJobs = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return jobs.filter((row) => {
            if (!needle) return true;
            const customer = customersById.get(row.customerId);
            return `${row.title} ${row.customerName} ${customer?.name || ""}`.toLowerCase().includes(needle);
        });
    }, [jobs, query, customersById]);

    const directChildren = useMemo(
        () => (focus ? (kidsByParent.get(focus.id) || []).filter((row) => !row.archived) : []),
        [focus, kidsByParent]
    );

    const childrenByKind = useMemo(() => {
        const grouped = { component: [], labor: [], purchase: [] };
        for (const child of directChildren) {
            const kind = lineKindOf(child);
            if (grouped[kind]) grouped[kind].push(child);
            else grouped.component.push(child);
        }
        return grouped;
    }, [directChildren]);

    const actionsByItem = useMemo(() => {
        const map = new Map();
        for (const entry of actions) {
            const key = entry.itemId || entry.jobId || "";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(entry);
        }
        return map;
    }, [actions]);

    const focusLogs = useMemo(() => {
        if (!focus) return [];
        const own = actionsByItem.get(focus.id) || [];
        if (focus.id === job?.id) {
            const unassigned = (actions || []).filter((row) => row.jobId === job.id && !row.itemId);
            const merged = [...own, ...unassigned.filter((row) => !own.some((x) => x.id === row.id))];
            return merged.sort((a, b) => String(b.occurredOn || "").localeCompare(String(a.occurredOn || "")));
        }
        return own;
    }, [actionsByItem, focus, job, actions]);

    const quotedHours = quotedHoursOf(focus);
    const loggedHours = Number(focus?.actualHours) || 0;
    const extraMaterials = Number(focus?.extraMaterialCost) || 0;

    function updateNav(nextJob, nextItem) {
        setSearchParams(() => {
            const next = new URLSearchParams();
            if (nextJob) next.set("job", nextJob);
            if (nextItem && nextItem !== nextJob) next.set("item", nextItem);
            return next;
        });
    }

    function selectJob(id) {
        updateNav(id, id);
    }

    function selectNode(item) {
        if (!item) return;
        updateNav(rootIdOf(item, byId), item.id);
    }

    function leaveJob() {
        updateNav("", "");
    }

    function goUp() {
        if (parentItem) selectNode(parentItem);
        else leaveJob();
    }

    function openLog(kind, itemId = "", jobOverride = null) {
        setLogKind(kind);
        setLogItemId(itemId);
        setLogJobOverride(jobOverride);
        setLogOpen(true);
    }

    async function saveLog(payload) {
        try {
            await act("save-action", { logged: payload });
            setLogOpen(false);
            setLogJobOverride(null);
            toast(payload.kind === "time" ? "Hours logged." : "Material logged.");
        } catch {
            /* toast already set */
        }
    }

    async function removeLog(entry) {
        try {
            await act("delete-action", { id: entry.id });
            toast("Removed.");
        } catch {
            /* toast already set */
        }
    }

    function lastLogFor(item) {
        const rows = actionsByItem.get(item.id) || [];
        return rows[0] || null;
    }

    const missingJob = Boolean(jobId) && !loading && !job;

    return (
        <Box sx={{ pb: 2 }}>
            {!(isMobile && job) ? (
                <PageHeader
                    badge="Operations"
                    title="Job Actions"
                    subtitle="Same job tree as the planner. Open a line, log time or extra materials, see what you actually did."
                    action={
                        job ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    startIcon={<MoreTimeIcon />}
                                    onClick={() => openLog("time", lineKindOf(focus) === "job" ? "" : focus?.id)}
                                >
                                    Log time
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Inventory2Icon />}
                                    onClick={() => openLog("material", lineKindOf(focus) === "job" ? "" : focus?.id)}
                                >
                                    Extra material
                                </Button>
                                <Button onClick={() => navigate(`/planner?job=${job.id}${focus && focus.id !== job.id ? `&item=${focus.id}` : ""}`)}>
                                    Open planner
                                </Button>
                            </Stack>
                        ) : null
                    }
                />
            ) : null}

            {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

            {!job ? (
                <Stack spacing={1.5}>
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search jobs…"
                        size="small"
                        sx={{ maxWidth: { md: 420 } }}
                    />
                    {visibleJobs.length ? (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                gap: 1.5,
                            }}
                        >
                            {visibleJobs.map((row) => (
                                <Box key={row.id}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
                                        {customersById.get(row.customerId)?.name || row.customerName || "General"}
                                    </Typography>
                                    <WorkCard
                                        item={row}
                                        childCount={(kidsByParent.get(row.id) || []).filter((child) => !child.archived).length}
                                        lastLog={lastLogFor(row)}
                                        onOpen={(node) => selectJob(node.id)}
                                        onLogTime={(node) => openLog("time", "", node)}
                                        onLogMaterial={(node) => openLog("material", "", node)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Paper sx={{ p: 3 }}>
                            <Typography color="text.secondary">
                                No jobs yet. Build the tree in Planner, then log shop time here.
                            </Typography>
                        </Paper>
                    )}
                </Stack>
            ) : null}

            {missingJob ? (
                <Paper sx={{ p: 3 }}>
                    <Typography sx={{ mb: 1.5 }}>That job is not on this list.</Typography>
                    <Button variant="contained" onClick={leaveJob}>
                        Back to jobs
                    </Button>
                </Paper>
            ) : null}

            {job && focus ? (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Button startIcon={<ArrowBackIcon />} onClick={goUp}>
                            {parentItem ? "Up" : "All jobs"}
                        </Button>
                        <Breadcrumbs sx={{ flex: 1, minWidth: 0 }}>
                            <Link component="button" underline="hover" color="inherit" onClick={leaveJob}>
                                Jobs
                            </Link>
                            {crumbs.slice(0, -1).map((crumb) => (
                                <Link
                                    key={crumb.id}
                                    component="button"
                                    underline="hover"
                                    color="inherit"
                                    onClick={() => selectNode(crumb)}
                                >
                                    {crumb.title}
                                </Link>
                            ))}
                            <Typography color="text.primary" sx={{ fontWeight: 700 }}>
                                {focus.title}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>

                    <Paper
                        sx={(t) => ({
                            ...panelCardSx(t),
                            ...kindSurfaceSx(t, lineKindOf(focus)),
                            p: { xs: 1.75, sm: 2.5 },
                        })}
                    >
                        <Typography variant="overline" color="text.secondary">
                            {customersById.get(job.customerId)?.name || job.customerName || "General"}
                            {focus.id !== job.id ? ` · ${lineKindLabel(lineKindOf(focus))}` : ""}
                        </Typography>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {focus.title}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 1.5 }}>
                            <LineKindChip kind={lineKindOf(focus)} />
                            {lineKindOf(focus) === "labor" && focus.laborCategory ? (
                                <Chip size="small" variant="outlined" label={categoryLabel(focus.laborCategory)} />
                            ) : null}
                        </Stack>
                        <HoursBar logged={loggedHours} quoted={quotedHours} />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                            <Metric label="Logged here" value={formatHours(loggedHours)} hint="This item and everything under it" />
                            <Metric
                                label="Quoted labor"
                                value={formatHours(quotedHours)}
                                hint={
                                    quotedHours
                                        ? `${loggedHours - quotedHours >= 0 ? "+" : ""}${formatHours(loggedHours - quotedHours)} vs quote`
                                        : "No hours on the quote"
                                }
                            />
                            <Metric label="Extra materials" value={formatMoney(extraMaterials)} hint="Logged against this branch" />
                        </Stack>
                        {isMobile ? (
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                <Button fullWidth variant="contained" startIcon={<MoreTimeIcon />} onClick={() => openLog("time", focus.id !== job.id ? focus.id : "")}>
                                    Time
                                </Button>
                                <Button fullWidth variant="outlined" startIcon={<Inventory2Icon />} onClick={() => openLog("material", focus.id !== job.id ? focus.id : "")}>
                                    Material
                                </Button>
                            </Stack>
                        ) : null}
                    </Paper>

                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Under this {lineKindLabel(lineKindOf(focus)).toLowerCase()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {directChildren.length
                                ? "Open a component, labor, or purchase — same layout as the planner."
                                : "Nothing nested here. Log against this line, or add children in the planner."}
                        </Typography>
                        {CHILD_SECTIONS.map((section) => {
                            const rows = childrenByKind[section.id] || [];
                            if (!rows.length) return null;
                            const filled = section.id !== "component";
                            return (
                                <Box
                                    key={section.id}
                                    sx={(t) => ({
                                        mb: 2,
                                        p: filled ? { xs: 1.25, sm: 1.75 } : 0,
                                        ...(filled ? kindSurfaceSx(t, section.id) : {}),
                                        borderRadius: 1,
                                    })}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={(t) => ({
                                            display: "block",
                                            mb: 1.25,
                                            fontWeight: 800,
                                            letterSpacing: "0.12em",
                                            color: plannerKindAccent(t, section.id),
                                        })}
                                    >
                                        {section.label} · {rows.length}
                                    </Typography>
                                    <Stack spacing={1.25}>
                                        {rows.map((child) => (
                                            <WorkCard
                                                key={child.id}
                                                item={child}
                                                childCount={(kidsByParent.get(child.id) || []).filter((row) => !row.archived).length}
                                                lastLog={lastLogFor(child)}
                                                onOpen={selectNode}
                                                onLogTime={(node) => openLog("time", node.id)}
                                                onLogMaterial={(node) => openLog("material", node.id)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                            Log on this line
                        </Typography>
                        {!focusLogs.length ? (
                            <Typography variant="body2" color="text.secondary">
                                Nothing logged directly here yet. Time on child items still rolls up in the bar above.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {focusLogs.map((entry) => {
                                    const linked = byId.get(entry.itemId);
                                    return (
                                        <Stack
                                            key={entry.id}
                                            direction="row"
                                            spacing={1}
                                            alignItems="flex-start"
                                            sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
                                        >
                                            <Chip
                                                size="small"
                                                color={entry.kind === "time" ? "info" : "warning"}
                                                label={entry.kind === "time" ? formatHours(entry.hours) : formatMoney(entry.amount)}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {entry.title || (entry.kind === "time" ? "Time" : "Material")}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {entry.occurredOn}
                                                    {linked && linked.id !== focus.id ? ` · ${linked.title}` : ""}
                                                    {entry.createdByName ? ` · ${entry.createdByName}` : ""}
                                                </Typography>
                                                {entry.body ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {entry.body}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                            <IconButton size="small" aria-label="Delete log" disabled={busy} onClick={() => removeLog(entry)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>
                </Stack>
            ) : null}

            <LogJobActionDialog
                open={logOpen}
                job={logJobOverride || job}
                items={
                    (logJobOverride || job)
                        ? items.filter((row) => {
                            const root = logJobOverride || job;
                            return row.id !== root.id && rootIdOf(row, byId) === root.id && !row.archived;
                        })
                        : []
                }
                initialKind={logKind}
                initialItemId={logItemId}
                busy={busy}
                onClose={() => {
                    setLogOpen(false);
                    setLogJobOverride(null);
                }}
                onSave={saveLog}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
